import {
  Box,
  CircularProgress,
  GridLegacy as Grid,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { blueGrey } from "@mui/material/colors";
import { mdHeight, xsHeight } from "components/AppBar";
import { Comments } from "components/Comments";
import { TabLinks, tabsOffset } from "components/TabLinks";
import { useTenant } from "hooks/useTenant";
import { useThemeMode } from "hooks/useThemeMode";
import { useEffect } from "react";
import {
  Outlet,
  useHref,
  useMatch,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useBreadcrumb } from "../../../../hooks/useBreadcrumbs";
import { kebabToTitleCase } from "../../../../util";
import { CohortSummary } from "./Activities/CohortSummary";
import { GeppettoCommand } from "./Activities/GeppettoCommand";
import { Agencies } from "./Agencies";
import { ContactDetails } from "./ContactDetails";
import { DownloadReport } from "./DownloadReport";
import { Extensions } from "./Extensions";
import { Facility } from "./Facility";
import { FulfillmentNote } from "./FulfillmentNote";
import { ImportantIdentifiers } from "./ImportantIdentifiers";
import { PortalCredentials } from "./PortalCredentials";
import { Reporting } from "./Reporting";
import { SupplementalAgencies } from "./SupplementalAgencies";
import { Tags } from "./Tags";
import { TenantContacts } from "./TenantContacts";
import { useFulfillmentDetails } from "./useFulfillmentDetails";

export function ReportDetails() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { themeMode } = useThemeMode();
  const isWideScreen = useMediaQuery("(min-width:1400px)");
  const { reportId } = useParams<{
    reportId: string;
  }>();
  const {
    data,
    loading,
    reportId: existingReportId,
    isUsingFacility,
  } = useFulfillmentDetails();
  const { tenantId, setCurrentTenantId } = useTenant();

  useEffect(() => {
    if (
      !loading &&
      data?.tierIIReport.tenantId &&
      data.tierIIReport.tenantId !== tenantId
    ) {
      setCurrentTenantId(data.tierIIReport.tenantId);
    }
  }, [data?.tierIIReport.tenantId, loading, setCurrentTenantId, tenantId]);

  const reportUrl = useHref(reportId ?? "");

  const reportKindString = kebabToTitleCase(
    data?.tierIIReport?.reportKind ?? ""
  );

  const facility = data?.tierIIReport.facility;
  const label = facility?.customerFacilityId
    ? `${facility?.name} - ${facility?.customerFacilityId}`
    : facility?.name;

  useBreadcrumb({
    label: label ?? "",
    to: reportUrl,
    addendum: reportKindString,
  });

  const reportBaseMatch = useMatch("/staff/fulfillment/r/:reportId");
  const facilityBaseMatch = useMatch(
    "/staff/fulfillment/f/:facilityId/:reportingYear"
  );
  const shouldDefaultToActivities = !!reportBaseMatch || !!facilityBaseMatch;

  useEffect(() => {
    if (shouldDefaultToActivities) {
      navigate("activities", { replace: true });
    }
  }, [navigate, shouldDefaultToActivities]);

  const activityTabs = [
    {
      title: "Activities",
      to: "activities",
    },
    {
      title: "Documents",
      to: "documents",
    },
    {
      title: "Payments",
      to: "payments",
    },
    {
      title: "Workflow Logs",
      to: "workflow-logs",
    },
    {
      title: "Jobs",
      to: "job",
    },
  ];

  // Track route changes
  const reportMatch = useMatch({
    path: "/staff/fulfillment/r/:reportId/:tab",
    end: false,
  });
  const facilityMatch = useMatch({
    path: "/staff/fulfillment/f/:facilityId/:reportingYear/:tab",
    end: false,
  });

  const match = reportMatch || facilityMatch;

  const selectedActivityTab =
    (match && activityTabs.find((t) => t.to === match.params.tab)?.to) ??
    activityTabs[0].to;

  // if we navigate to a facility page for a given reporting year,
  // we want to check and see if we already have an ANNUAL report for that year. If so,
  // we don't want to show the facility UI, but instead redirect to the report page.
  useEffect(() => {
    if (isUsingFacility && existingReportId) {
      navigate(
        `/staff/fulfillment/r/${existingReportId}/${selectedActivityTab}`
      );
    }
  }, [isUsingFacility, existingReportId, navigate, selectedActivityTab]);

  return (
    <>
      <TabLinks value={selectedActivityTab} tabs={activityTabs} />
      <Stack
        p={3}
        sx={{
          background: blueGrey[themeMode === "light" ? 50 : 900],
          minHeight: {
            xs: `calc(100vh - ${theme.spacing(xsHeight + tabsOffset)})`,
            md: `calc(100vh - ${theme.spacing(mdHeight + tabsOffset)})`,
          },
        }}
      >
        {!data || loading ? (
          <Stack
            display="flex"
            alignItems="center"
            justifyContent="center"
            flex={1}
          >
            <CircularProgress />
          </Stack>
        ) : (
          <Grid container spacing={2}>
            <Grid
              item
              container
              rowGap={theme.spacing(2)}
              md={isWideScreen ? 8 : 12}
              xs={12}
              flexDirection="column"
            >
              <FulfillmentNote />

              <Outlet />

              {/* Comments and Tags side by side */}
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Comments />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Tags />
                </Grid>
              </Grid>

              {/* Cohorts */}
              <CohortSummary
                cohortMemberships={data.tierIIReport.cohortMemberships}
              />

              {/* In single-column mode, Facility/StatePortal and Reporting/ImportantIdentifiers appear here */}
              <Box
                sx={{
                  display: {
                    xs: "grid",
                    sm: "grid",
                    md: isWideScreen ? "none" : "grid",
                  },
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <Stack spacing={2}>
                  <PortalCredentials />
                  <DownloadReport />
                </Stack>
                <Facility />
              </Box>

              <Box
                sx={{
                  display: {
                    xs: "grid",
                    sm: "grid",
                    md: isWideScreen ? "none" : "grid",
                  },
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <Reporting />
                <ImportantIdentifiers />
              </Box>

              {/* LEPC and Fire Department */}
              <Agencies />

              {/* Supplemental LEPC and Fire Department */}
              <SupplementalAgencies />

              {/* Contacts */}
              <ContactDetails />

              {/* State Fields */}
              <Extensions />

              {/* In single-column mode, TenantContacts and GeppettoCommand appear here */}
              <Box
                sx={{
                  display: { xs: "block", md: isWideScreen ? "none" : "block" },
                }}
              >
                <Stack spacing={2}>
                  <TenantContacts />
                  <GeppettoCommand />
                </Stack>
              </Box>
            </Grid>
            <Grid
              item
              md={isWideScreen ? 4 : 12}
              xs={12}
              sx={{
                display: { xs: "none", md: isWideScreen ? "block" : "none" },
              }}
            >
              <Stack spacing={2}>
                {/* In 2-column mode (xl), sections are separate Stack items */}
                <PortalCredentials />
                <DownloadReport />
                <Facility />
                <Reporting />
                <ImportantIdentifiers />
                <TenantContacts />
                <GeppettoCommand />
              </Stack>
            </Grid>
          </Grid>
        )}
      </Stack>
    </>
  );
}
